import { useMutation } from '@apollo/client'
import { Box, Spinner } from '@factorialco/deprecated-design-system'
import {
  NotificationsNotifierScopesDocument,
  NotificationsUpdateNotifierScopeDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import bookkeepers from 'modules/bookkeepers/store/collections/bookkeepers'
import { PreferencesList } from 'modules/notifications/components/Preferences/PreferencesList'
import notifiers from 'modules/notifications/store/collections/notifiers'
import userPreferences from 'modules/notifications/store/collections/userPreferences'
import type Notifier from 'modules/notifications/store/models/Notifier'
import type UserPreference from 'modules/notifications/store/models/UserPreference'

type Props = {
  search: string
}

export const UserPreferences = observer(({ search }: Props) => {
  const { loading: loadingScopes, data } = useQuery(NotificationsNotifierScopesDocument)
  const {
    access: { id: currentAccessId },
  } = useCurrentSession()
  const [updateScope] = useMutation(NotificationsUpdateNotifierScopeDocument, {
    refetchQueries: [{ query: NotificationsNotifierScopesDocument }],
  })
  const { loading } = useAsyncData(
    () => Promise.all([notifiers.fetch(), userPreferences.fetch(), bookkeepers.fetch()]),
    []
  )

  const handlePreferenceUpdated = async (preference: UserPreference, newValue: boolean) => {
    await preference.updateValue(newValue)
  }

  const handleScopeUpdated = async (notifier: string, newValue: string) => {
    await updateScope({
      variables: {
        notifier,
        value: newValue,
      },
    })
  }

  const filteredNotifiers = () => {
    const isBookkeeper = !!bookkeepers.find({
      access_id: currentAccessId,
    })

    if (isBookkeeper) {
      return notifiers.models.filter(
        (notifier: Notifier) =>
          !['employee_updates_status_and_messages', 'bookkeeper_payroll_cycles'].includes(
            notifier.category
          )
      )
    }

    return notifiers.models
  }

  return loading || loadingScopes ? (
    <Box width="full" paddingY="s32" alignItems="center">
      <Spinner />
    </Box>
  ) : (
    <PreferencesList
      notifiers={filteredNotifiers()}
      preferences={userPreferences.models}
      notifierScopes={data?.notifications.accessNotifierScopes || []}
      search={search}
      onPreferenceUpdated={handlePreferenceUpdated}
      onScopeUpdated={handleScopeUpdated}
    />
  )
})
